"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import StepIndicator from "./step-indicator";

import { animate, fadeIn } from "@/lib/animation-utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import IdentificationStep from "@/components/form-steps/IdentificationStep";
import ReproductionCycleStep from "@/components/form-steps/ReproductionCycleStep";
import PregnancyResultsStep from "@/components/form-steps/PregnancyResultsStep";
import CalfBirthDetailsStep from "@/components/form-steps/CalfBirthDetailsStep";
import CalfDevelopmentStep from "@/components/form-steps/CalfDevelopmentStep";
import WeaningInformationStep from "@/components/form-steps/WeaningInformationStep";
import PhysicalAssessmentStep from "@/components/form-steps/PhysicalAssessmentStep";
import FormSummaryStep from "@/components/form-steps/FormSummaryStep";
import { apiClient } from "@/api/api.ts";
import { useAnimalUpdateRecord } from "@/hooks/useAnimalUpdateRecord";

// Define the form schema
const formSchema = z.object({
  // Step 1: Cow Identification
  cowEarTag: z.string().min(1, "Ear tag is required"),
  cowManagement: z.string().min(1, "Management information is required"),
  cowBreed: z.string().min(1, "Breed is required"),

  // Step 2: Reproduction Cycle
  syncYear: z.string().optional(),
  syncMonth: z.string().optional(),
  estrousCycle: z.string().optional(),
  bcs: z.string().optional(),
  pregnancyDiagnosis: z.string().optional(),

  // Step 3: Pregnancy Results
  finalPregnancyCheck: z.enum(["", "Positive", "Negative"]).optional(),
  observationFinalPregnancyCheck: z.string().optional(),

  // Step 4: Calf Birth Details
  calfBirthDate: z.string().optional(),
  calfBirthYear: z.string().optional(),
  calfBirthMonth: z.string().optional(),
  calfNumber: z.string().optional(),
  calfSex: z.enum(["", "Male", "Female"]).optional(),
  calfCoatColor: z.string().optional(),
  calfBirthWeight: z.string().optional(),
  calfType: z.enum(["", "Natural", "AI", "ET"]).optional(),

  vaccination: z
    .array(z.string())
    .optional()
    .transform((val) => val ?? "not provided"),

  // Step 5: Calf Development
  gestationPeriod: z.string().optional(),
  calfBirthNotes: z.string().optional(),
  calfDeathDate: z.string().optional(),
  calfDeathNotes: z.string().optional(),

  // Step 6: Weaning Information
  weaningDate: z.string().optional(),
  weaningWeight: z.string().optional(),
  weaningLocation: z.string().optional(),

  // Step 7: Physical Assessment
  tickCount: z.string().optional(),
  headEvaluation: z.string().optional(),
  bodyType: z.string().optional(),
  color: z.string().optional(),
  muscleDevelopment: z.string().optional(),
  umbilicalCondition: z.string().optional(),
  bodyStructure: z.string().optional(),
  generalConformation: z.string().optional(),
  earTagVerified: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

export default function CattleForm({ id }: { id?: string }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const totalSteps = 8; // Including summary step

  const queryClient = useQueryClient();
  const { data: animal, isLoading, isError, refetch } = useAnimalUpdateRecord(id);

  const { mutateAsync } = useMutation({
    mutationKey: ["addRecipientEvaluation"],
    mutationFn: async (data: any) => {
      try {
        const response = await apiClient.post(
          "/admin/recipientEvaluation",
          data,
        );

        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onMutate: async (data: any) => {
      await queryClient.cancelQueries({ queryKey: ["animals"] });
      const previousAnimals = queryClient.getQueryData<any>(["animals"]);

      queryClient.setQueryData<any>(["animals"], (old: any) => {
        if (!old) return [];

        return old.map((animal: any) =>
          animal._id === id
            ? {
                ...animal,
                recipientEvaluation: [data],
              }
            : animal,
        );
      });

      return { previousAnimals };
    },
    onSuccess: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      toast.success("Recipient evaluation saved successfully");
    }, [queryClient]),
    onError: useCallback(
      (error: Error, _variables: any, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to save recipient evaluation. Your edits have been kept for retry.");
        queryClient.setQueryData(["animals"], context?.previousAnimals);
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["adminAnimal", id] });
      queryClient.invalidateQueries({ queryKey: ["SilaAnimal", id] });
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      cowEarTag: "",
      cowManagement: "",
      cowBreed: "",
      syncYear: "",
      syncMonth: "",
      estrousCycle: "",
      bcs: "",
      pregnancyDiagnosis: "",
      finalPregnancyCheck: "",
      observationFinalPregnancyCheck: "",
      calfBirthDate: "",
      calfBirthYear: "",
      calfBirthMonth: "",
      calfNumber: "",
      calfSex: "",
      calfCoatColor: "",
      calfBirthWeight: "",
      calfType: "",
      gestationPeriod: "",
      calfBirthNotes: "",
      calfDeathDate: "",
      calfDeathNotes: "",
      weaningDate: "",
      weaningWeight: "",
      weaningLocation: "",
      tickCount: "",
      headEvaluation: "",
      bodyType: "",
      color: "",
      muscleDevelopment: "",
      umbilicalCondition: "",
      bodyStructure: "",
      generalConformation: "",
      earTagVerified: false,
      vaccination: [],
    },
  });

  useEffect(() => {
    const evaluation = Array.isArray(animal?.recipientEvaluation)
      ? animal.recipientEvaluation[0] : animal?.recipientEvaluation;
    if (evaluation) {
      const normalized = Object.fromEntries(Object.entries(evaluation).map(([key, value]) =>
        [key, typeof value === "number" ? String(value) : value ?? ""],
      ));
      form.reset({ ...form.getValues(), ...normalized,
        vaccination: Array.isArray(evaluation.vaccination) ? evaluation.vaccination : [],
        earTagVerified: evaluation.earTagVerified === true,
      }, { keepDirtyValues: true });
    }
  }, [animal, form]);

  const animateTransition = (direction: "next" | "prev") => {
    if (!formContainerRef.current || isAnimating) return;

    setIsAnimating(true);

    const container = formContainerRef.current;

    // First animation - fade out and slide
    animate(
      container,
      {
        opacity: 0,
        translateX: direction === "next" ? -20 : 20,
      },
      {
        duration: 300,
        easing: "ease-in-out",
        onComplete: () => {
          // Update step
          setCurrentStep((prev) =>
            direction === "next"
              ? Math.min(prev + 1, totalSteps)
              : Math.max(prev - 1, 1),
          );

          // Reset position for next animation
          container.style.transform = `translateX(${direction === "next" ? 20 : -20}px)`;

          // Second animation - fade in and slide back
          requestAnimationFrame(() => {
            animate(
              container,
              {
                opacity: 1,
                translateX: 0,
              },
              {
                duration: 300,
                easing: "ease-in-out",
                onComplete: () => {
                  setIsAnimating(false);
                },
              },
            );
          });
        },
      },
    );
  };

  const nextStep = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await form.trigger(fieldsToValidate as any);

    if (isValid) {
      animateTransition("next");
    }
  };

  const prevStep = () => {
    animateTransition("prev");
  };

  const getFieldsForStep = (step: number): (keyof FormValues)[] => {
    switch (step) {
      case 1:
        return ["cowEarTag", "cowManagement", "cowBreed"];
      case 2:
        return [
          "syncYear",
          "syncMonth",
          "estrousCycle",
          "bcs",
          "pregnancyDiagnosis",
        ];
      case 3:
        return ["finalPregnancyCheck", "observationFinalPregnancyCheck"];
      case 4:
        return [
          "calfBirthYear",
          "calfBirthMonth",
          "calfNumber",
          "calfSex",
          "calfCoatColor",
          "calfBirthWeight",
          "calfType",
        ];
      case 5:
        return [
          "gestationPeriod",
          "calfBirthNotes",
          "calfDeathDate",
          "calfDeathNotes",
        ];
      case 6:
        return ["weaningDate", "weaningWeight", "weaningLocation"];
      case 7:
        return [
          "tickCount",
          "headEvaluation",
          "bodyType",
          "color",
          "muscleDevelopment",
          "umbilicalCondition",
          "bodyStructure",
          "generalConformation",
          "earTagVerified",
        ];
      default:
        return [];
    }
  };

  const onSubmit = async () => {
    const formData = { ...form.getValues(), id };
    const update = mutateAsync(formData);
    toast.promise(update, {
      loading: "Saving recipient evaluation...",
      success: "Recipient evaluation saved.",
      error: "Failed to save recipient evaluation.",
    });
    try {
      await update;
      form.reset(formData);
    } catch {
      // Keep the edited values available for retry; the mutation reports errors.
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <IdentificationStep />;
      case 2:
        return <ReproductionCycleStep />;
      case 3:
        return <PregnancyResultsStep />;
      case 4:
        return <CalfBirthDetailsStep />;
      case 5:
        return <CalfDevelopmentStep />;
      case 6:
        return <WeaningInformationStep />;
      case 7:
        return <PhysicalAssessmentStep />;
      case 8:
        return <FormSummaryStep data={form.getValues()} />;
      default:
        return null;
    }
  };

  // Initial animation on mount
  useEffect(() => {
    if (formContainerRef.current) {
      fadeIn(formContainerRef.current, {
        duration: 800,
        easing: "ease-out",
      });
    }
  }, []);

  if (!id) return <p role="alert">Animal ID is missing.</p>;
  if (isLoading) return <p>Loading recipient evaluation...</p>;
  if (isError) return <div role="alert">Unable to load recipient evaluation. <Button onClick={() => refetch()}>Retry</Button></div>;

  return (
    <div className="w-full space-y-6 z-50 px-0">
      <StepIndicator currentStep={currentStep} totalSteps={totalSteps - 1} />

      <Card className="w-full overflow-hidden border-slate-200 p-0 shadow-lg">
        <CardContent className="px-2">
          <div ref={formContainerRef} className="min-h-[500px] py-6 px-0">
            <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
              toast.error(Object.values(errors).map(error => error?.message).filter(Boolean).join("; "));
            })}>
              <FormProvider {...form}>
                {renderStepContent()}

                <div className="mt-8 flex justify-between">
                  {currentStep > 1 && (
                    <Button
                      className="gap-2 bg-[#008c8fff]"
                      disabled={isAnimating}
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Previous
                    </Button>
                  )}

                  {currentStep < totalSteps ? (
                    <Button
                      className="ml-auto gap-2 bg-[#008c8fff]"
                      disabled={isAnimating}
                      type="button"
                      onClick={nextStep}
                    >
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      className="ml-auto gap-2"
                      disabled={form.formState.isSubmitting}
                      type="submit"
                    >
                      <Check className="h-4 w-4" />
                      Submit
                    </Button>
                  )}
                </div>
              </FormProvider>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
